/* eslint-disable react/no-danger */
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Col, Modal, Row, Spin, Tooltip } from 'antd';
import Button from 'components/generalUI/Button';
import useListOpenDays from 'apollo/resolvers/openDays/list';
import { LoadingOutlined } from '@ant-design/icons';
import formatDateLocaleES from 'utils/dates/formatDateLocaleES';
import isDayInArray from 'utils/dates/isDayInArray';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import getLastOpenDay from 'utils/dates/getLastOpenDay';

import captureEvent from 'utils/post-hog';
import * as events from 'utils/post-hog/events';

import { formatDateToChileanTimeZoneString } from 'utils/dates/formatDateToChileanTimeZone';
import styles from './OpenDayPicker.module.scss';
import { OpenDayPickerProps, OpenDayPickerHeaderProps } from './types';
import OpenDayPickerHeader from './OpenDayPickerHeader';

const OpenDayPickerHeaderSub = ({
  value,
  onChange,
  lastOpenDayDate,
  setSelectedMonth,
  selectedMonth,
  hideTitle,
  onVisibleMonthChange,
}: OpenDayPickerHeaderProps) => {
  return (
    <OpenDayPickerHeader
      value={value}
      onChange={onChange}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      lastOpenDayDate={lastOpenDayDate}
      setSelectedMonth={setSelectedMonth}
      selectedMonth={selectedMonth}
      hideTitle={hideTitle}
      onVisibleMonthChange={onVisibleMonthChange}
    />
  );
};

const OpenDayPicker: FC<OpenDayPickerProps> = ({
  startDate,
  endDate,
  redirectUrlType,
  onSelectDate,
  selectedDate,
  unavailableDates,
  soldOutDates,
  onMonthChange,
}) => {
  const { t } = useTranslation();
  const { data } = useListOpenDays(startDate, endDate);
  const [possibleDate, setPossibleDate] = useState<string>();
  const [openDays, setOpenDays] = useState(data?.listOpenDays);
  const [selectedMonth, setSelectedMonth] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [url, setUrl] = useState<string>();

  const currentMomentDate = moment().startOf('day');
  const lastOpenDay = getLastOpenDay(openDays);
  const lastOpenDayDate = lastOpenDay && new Date(lastOpenDay.day);

  const isDayInList = (dates: string[] | undefined, chileanDateString: string) =>
    !!dates?.some((date) => date.split('T')[0] === chileanDateString.split('T')[0]);

  const isDaySoldOut = (chileanDateString: string) =>
    isDayInList(soldOutDates, chileanDateString);

  const isDayUnavailable = (chileanDateString: string) =>
    isDaySoldOut(chileanDateString) || isDayInList(unavailableDates, chileanDateString);

  const disabledDate = (date: Moment) => {
    const chileanDate = formatDateToChileanTimeZoneString(
      date.clone().startOf('day').toDate()
    );
    const isNotOpenDay = !!openDays && isDayInArray(moment(chileanDate), openDays);

    return isNotOpenDay || isDayUnavailable(chileanDate);
  };

  const onVisibleMonthChange = (month: number, year: number) => {
    if (!onMonthChange) return;
    const start = moment({ year, month, date: 1 }).startOf('day');
    const end = start.clone().endOf('month').startOf('day');
    onMonthChange(
      formatDateToChileanTimeZoneString(start.toDate()),
      formatDateToChileanTimeZoneString(end.toDate())
    );
  };

  const onPanelChange = (date: moment.Moment) => {
    if (openDays && !isDayInArray(date, openDays)) {
      const chileanDateString = formatDateToChileanTimeZoneString(date.toDate());
      if (isDayUnavailable(chileanDateString)) return;

      setPossibleDate(chileanDateString);
      setUrl(`/${redirectUrlType}?date=${chileanDateString}`);
      if (onSelectDate) onSelectDate(chileanDateString);
    } else if (openDays && isDayInArray(date, openDays)) {
      setPossibleDate(undefined);
    }
  };

  useEffect(() => {
    formatDateLocaleES();
    if (data) {
      const formattedOpenDays = data?.listOpenDays.map((day) => {
        return {
          ...day,
          day: day.day,
        };
      });
      setOpenDays(formattedOpenDays);
    }
  }, [data]);

  const dateFullCellRender = (value: Moment) => {
    const startOfDay = value.startOf('day');
    const chileanDate = formatDateToChileanTimeZoneString(startOfDay.toDate());

    const chileanMomentDate = moment(chileanDate);

    const monthDay = chileanMomentDate.date();

    const isInTheFutureOrCurrent = chileanMomentDate.isSameOrAfter(currentMomentDate);

    const isBeforeLastOpenDay =
      lastOpenDayDate && chileanMomentDate.toDate() < lastOpenDayDate;
    const isNotOpenDay = openDays && isDayInArray(chileanMomentDate, openDays);

    if (selectedMonth === undefined || chileanMomentDate.month() !== selectedMonth) {
      return null;
    }

    if (isNotOpenDay) {
      return (
        <div className="ant-picker-cell-disabled">
          {isInTheFutureOrCurrent ? (
            <Tooltip
              title={
                isBeforeLastOpenDay
                  ? t('components.openDayPicker.parkClosed')
                  : t('components.openDayPicker.notYetAvailable')
              }
            >
              <div
                className={`ant-picker-cell-inner-disabled ant-picker-calendar-date ${
                  isBeforeLastOpenDay ? 'closed-day' : ''
                }`}
              >
                <div className="ant-picker-calendar-date-value">{monthDay}</div>
              </div>
            </Tooltip>
          ) : (
            <div className="ant-picker-cell-inner-disabled ant-picker-calendar-date past-day">
              <div className="ant-picker-calendar-date-value">{monthDay}</div>
            </div>
          )}
        </div>
      );
    }
    const isSelectedDay =
      !!selectedDate && selectedDate.split('T')[0] === chileanDate.split('T')[0];
    const isUnavailableDay = isDayUnavailable(chileanDate);

    const stateClass = [
      isSelectedDay ? 'selected-day' : '',
      !isInTheFutureOrCurrent ? 'past-day' : '',
      isInTheFutureOrCurrent && !isSelectedDay && isUnavailableDay
        ? 'unavailable-day'
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    const dayCell = (
      <div className={`ant-picker-cell-inner ant-picker-calendar-date ${stateClass}`}>
        <div className="ant-picker-calendar-date-value">{monthDay}</div>
      </div>
    );

    if (isInTheFutureOrCurrent && isUnavailableDay) {
      return (
        <Tooltip
          title={
            isDaySoldOut(chileanDate)
              ? t('components.openDayPicker.soldOut')
              : t('components.openDayPicker.notEnoughCapacity')
          }
        >
          {dayCell}
        </Tooltip>
      );
    }

    return dayCell;
  };

  return (
    <>
      <Modal
        footer={null}
        className={styles.ImportantInformationModal}
        visible={isModalOpen}
        title={t('pages.ticketSelection.importantInformation')}
        onCancel={() => setIsModalOpen(false)}
      >
        <Row className={styles.TextContainer}>
          <Col span={24}>
            <p
              dangerouslySetInnerHTML={{
                __html: t('pages.ticketSelection.onlyOnlineSales'),
              }}
            />
            <p
              dangerouslySetInnerHTML={{
                __html: t('pages.ticketSelection.childrenFree'),
              }}
            />
          </Col>
        </Row>
      </Modal>
      {openDays ? (
        <div className={styles.OpenDayContainer}>
          <Calendar
            className={`${styles.OpenDayPicker} ${
              onSelectDate ? styles.OpenDayPickerFullWidth : ''
            }`}
            mode="month"
            fullscreen={false}
            validRange={[currentMomentDate, moment(lastOpenDayDate).add(1, 'day')]}
            disabledDate={disabledDate}
            onSelect={onPanelChange}
            headerRender={({ value, onChange }) =>
              OpenDayPickerHeaderSub({
                value,
                onChange,
                lastOpenDayDate,
                setSelectedMonth,
                selectedMonth,
                hideTitle: !!onSelectDate,
                onVisibleMonthChange,
              })
            }
            dateFullCellRender={dateFullCellRender}
          />

          {!onSelectDate && (
            <>
              <Button
                className={styles.OpenDayPickerButton}
                size="large"
                disabled={!possibleDate}
                href={url}
                onClick={() =>
                  captureEvent(events.toSelectEntrancesButtonClicked, {
                    flow: redirectUrlType?.startsWith('confirmChange')
                      ? events.flows.purchaseChange
                      : events.flows.purchase,
                  })
                }
              >
                {t('components.openDayPicker.select')}
              </Button>
              <Button type="link" onClick={() => setIsModalOpen(true)}>
                {t('components.openDayPicker.importantInfo')}
              </Button>
            </>
          )}
        </div>
      ) : (
        <Spin indicator={<LoadingOutlined className={styles.Loader} spin />} />
      )}
    </>
  );
};

export default OpenDayPicker;
